<script setup lang="ts">
    import ArrowBackIcon from "@/assets/arrow_back_dark.svg";
    import ChainIcon from "@/assets/chain_dark.svg";
    import KontrolleratIcon from "@/assets/kontrollerat.svg";
    import LockIcon from "@/assets/lock_red.svg";
    import LockUserIcon from "@/assets/lock_user_red.svg";
    import MakuleradIcon from "@/assets/makulerad.svg";
    import { CellContentAlignment } from "@/components/KTable/helpers";
    import KCell from "@/components/KTable/KCell.vue";
    import { NllLockSubject, NllLockType } from "@/store/modules/recept/helpers";
    import { checkNllLocks, NllLocksDisplay } from "@/tabs/Receptforskrivning/helpers";
    import { Forskrivningsstatus, ReceptforskrivningDisplay } from "@/tabs/Receptforskrivning/types";
    import { computed, toRefs } from "vue";

    const props = withDefaults(
        defineProps<{
            data?: {
                kontrollerat?: boolean;
                kedja?: boolean;
                narkotikaNiva?: number;
                parallell?: boolean;
                sparrar?: Array<{
                    legal_ground: keyof typeof NllLockType;
                    subject_type: keyof typeof NllLockSubject;
                    status: string;
                }>;
                forskrivningsstatus?: string;
                kedjaIndex?: number;
            };
            alignContent?: CellContentAlignment;
        }>(),
        {
            alignContent: CellContentAlignment.Center,
            narkotikaNiva: -1,
        }
    );

    const { data } = toRefs(props);

    const locks = computed<NllLocksDisplay | null>(() => {
        return checkNllLocks({ sparrar: data?.value?.sparrar as ReceptforskrivningDisplay["sparrar"] });
    });

    const getIntegritetLabel = () => {
        const integritetLabel = "Integritetsspärr";
        const behandlingsorsakIntegritetLabel = "Behandlingsorsak spärrad av patient";

        if (locks?.value?.integritet && locks?.value?.behandlingsorsakIntegritet) {
            return `${integritetLabel} och ${behandlingsorsakIntegritetLabel}`;
        } else if (locks?.value?.integritet) {
            return integritetLabel;
        } else if (locks?.value?.behandlingsorsakIntegritet) {
            return behandlingsorsakIntegritetLabel;
        }
    };

    const getSekretessLabel = () => {
        const behandlingsorsakLabel = "Behandlingsorsak spärrad för patient";
        const sekretessVardnadshavareLabel = "Spärrad för vårdnadshavare";

        if (locks?.value?.behandlingsorsak && locks?.value?.sekretessVardnadshavare) {
            return `${behandlingsorsakLabel} och ${sekretessVardnadshavareLabel}`;
        } else if (locks?.value?.behandlingsorsak) {
            return behandlingsorsakLabel;
        } else if (locks?.value?.sekretessVardnadshavare) {
            return sekretessVardnadshavareLabel;
        }
    };
</script>

<template>
    <KCell :data="data" class="cell-recept-ikoner" contentDirection="vertical" :alignContent="alignContent">
        <template #content="slotProps">
            <div
                v-if="locks?.integritet || locks?.behandlingsorsakIntegritet"
                class="image-container"
                v-tooltip="{
                    content: getIntegritetLabel(),
                }"
            >
                <img :src="LockUserIcon" />
            </div>
            <div
                v-if="locks?.behandlingsorsak || locks?.sekretessVardnadshavare"
                class="image-container"
                v-tooltip="{
                    content: getSekretessLabel(),
                }"
            >
                <img :src="LockIcon" />
            </div>
            <div v-if="data?.parallell" class="image-container">
                <img :src="ArrowBackIcon" />
            </div>
            <div
                v-if="data?.kedja"
                :class="[
                    'image-container',
                    {
                        'double-digit': data?.kedjaIndex && data?.kedjaIndex > 9,
                    },
                ]"
            >
                <img :src="ChainIcon" />
                <span>{{ data?.kedjaIndex }}</span>
            </div>
            <div v-if="data?.kontrollerat" class="image-container">
                <img :src="KontrolleratIcon" />
            </div>
            <div v-if="data?.forskrivningsstatus === Forskrivningsstatus.Makulerad" class="image-container">
                <img :src="MakuleradIcon" />
            </div>
        </template>
    </KCell>
</template>

<style scoped lang="scss">
    .cell-recept-ikoner {
        .image-container {
            display: grid;
            background-color: white;
            border-radius: 6px;
            place-items: center;
            width: 23px;
            height: 23px;
            box-sizing: border-box;
            padding: 3px;
            position: relative;

            img {
                width: 100%;
            }

            &:has(span) {
                padding: 1px;
                grid-template-columns: 1.5fr 1fr;
                grid-template-rows: 1.5fr 1fr;
                position: relative;

                img {
                    grid-column: 1;
                    grid-row: 1;
                }

                span {
                    grid-column: 2;
                    grid-row: 2;
                    font-size: 12px;
                    line-height: 1;
                    // Låter span flyta ut lite för att visuellt placeras bättre
                    margin-left: -3px;
                    margin-top: -4px;
                }

                &.double-digit {
                    grid-template-columns: 1.2fr 1fr;
                    grid-template-rows: 1.2fr 1fr;

                    span {
                        font-size: 11px;
                        // Låter span flyta ut lite för att visuellt placeras bättre
                        margin-left: -3px;
                        margin-top: -1px;
                    }
                }
            }
        }

        ::v-deep .content {
            grid-template-columns: repeat(auto-fill, 23px);
            grid-column-gap: 1px;
            grid-row-gap: 1px;
        }
    }
</style>
